import { App, Button, Drawer, Form, Space } from 'antd';
import { useCallback, useState } from 'react';
import {
  buildProjectPayload,
  ProjectFormFields,
  projectFormValuesFromDetail,
  type ProjectFormValues,
} from '@/pages/admin/projects/ProjectFormFields';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectDetail } from '@/types/project';

interface ProjectEditDrawerProps {
  open: boolean;
  project: ProjectDetail | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ProjectEditDrawer({ open, project, onClose, onSaved }: ProjectEditDrawerProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProjectFormValues>();
  const [saving, setSaving] = useState(false);

  const applyFormValues = useCallback(() => {
    if (!project) {
      return;
    }
    form.setFieldsValue(projectFormValuesFromDetail(project));
  }, [form, project]);

  const handleSubmit = async () => {
    if (!project) {
      return;
    }

    const values = await form.validateFields();
    setSaving(true);
    try {
      await projectsApi.updateProject(project.id, buildProjectPayload(values, 'update'));
      message.success('项目已更新');
      onSaved();
      onClose();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '更新项目失败';
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  const formKey = project ? `edit-${project.id}` : 'edit-empty';
  const initialValues = project ? projectFormValuesFromDetail(project) : undefined;

  return (
    <Drawer
      title="编辑项目"
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
      afterOpenChange={(visible) => {
        if (visible) {
          applyFormValues();
        }
      }}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSubmit()}>
            保存
          </Button>
        </Space>
      }
    >
      {project && initialValues ? (
        <Form
          key={formKey}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={initialValues}
        >
          <ProjectFormFields form={form} projectId={project.id} />
        </Form>
      ) : null}
    </Drawer>
  );
}
